import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

const USFlag = () => (
  <svg
    className="tw-w-4 tw-h-4 tw-rounded"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect width="24" height="24" fill="#B22234" />
    <path
      d="M0 2.77h24M0 5.54h24M0 8.31h24M0 11.08h24M0 13.85h24M0 16.62h24M0 19.39h24"
      stroke="#fff"
      strokeWidth="1.85"
    />
    <rect width="10" height="12.92" fill="#3C3B6E" />
  </svg>
);

const ESFlag = () => (
  <svg
    className="tw-w-4 tw-h-4 tw-rounded"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect width="24" height="6" fill="#AA151B" />
    <rect y="6" width="24" height="12" fill="#F1BF00" />
    <rect y="18" width="24" height="6" fill="#AA151B" />
  </svg>
);

const languages = {
  en: { name: "English", flag: <USFlag /> },
  es: { name: "Español", flag: <ESFlag /> },
};

const LanguageToggle = ({ compact = false }) => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const toggleDropdown = () => setIsOpen(!isOpen);

  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem("appLanguage", lang);
    setIsOpen(false);
  };

  useEffect(() => {
    const savedLang = localStorage.getItem("appLanguage");
    if (savedLang && savedLang !== i18n.language) {
      i18n.changeLanguage(savedLang);
    }
  }, [i18n]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const currentLanguage = languages[i18n.language] || languages.en;

  return (
    <div className="tw-relative" ref={dropdownRef}>
      {/* Button to open the dropdown */}
      <button
        onClick={toggleDropdown}
        className={`tw-flex tw-items-center tw-gap-2 tw-bg-white tw-border tw-border-gray-300 tw-rounded-lg tw-shadow-sm hover:tw-bg-gray-50 hover:tw-shadow-md tw-transition-all focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-ring-opacity-50 ${
          compact ? "tw-px-3 tw-py-2" : "tw-px-4 tw-py-2.5"
        }`}
        aria-label="Change language"
      >
        {currentLanguage.flag}
        {!compact && (
          <span className="tw-text-sm tw-font-medium tw-text-gray-700">
            {currentLanguage.name}
          </span>
        )}
        <svg
          className={`tw-w-4 tw-h-4 tw-text-gray-500 tw-transition-transform ${
            isOpen ? "tw-rotate-180" : ""
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="tw-absolute tw-right-0 tw-mt-2 tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-shadow-xl tw-overflow-hidden tw-z-50 tw-min-w-[180px]">
          {Object.entries(languages).map(([code, lang]) => (
            <button
              key={code}
              className={`tw-w-full tw-flex tw-items-center tw-gap-3 tw-px-4 tw-py-3 tw-cursor-pointer tw-transition-colors ${
                i18n.language === code
                  ? "tw-bg-blue-50 tw-text-blue-700"
                  : "hover:tw-bg-gray-50 tw-text-gray-700"
              }`}
              onClick={() => changeLanguage(code)}
            >
              {lang.flag}
              <span className="tw-text-sm tw-font-medium">{lang.name}</span>
              {i18n.language === code && (
                <svg
                  className="tw-w-4 tw-h-4 tw-ml-auto tw-text-blue-600"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageToggle;
